// @ts-strict-ignore
import type { MatrixModel } from 'flow/store/engine/types';
import type { Axis } from '../AxisHeaderDropdown';
import { AXIS_HIGH_UNBOUNDED_NUMBER, AXIS_LOW_UNBOUNDED_NUMBER } from '~/dashboard/views/workflows/service/actions';

export const getValueOrUndefined = (value: string | number) => (value !== null
  ? value : undefined);

export const getDefaultRangeText = (
  axis: Axis, axisOrder: MatrixModel['axis']['x' | 'y']['order'], isLastBucket: boolean,
) => {
  if (isLastBucket) {
    return (
      // for all bounded ranges - regardless of axisOrder,
      // the last bucket is inclusive of the last value (i.e. <= X <= value)
      axisOrder === 'asc'
        ? `<= ${axis} <=` // value <= X <= value
        : `> ${axis} >=` // value > X >= value
    );
  }
  return (
    axisOrder === 'asc'
      ? `<= ${axis} <` // value <= X < value
      : `> ${axis} >=` // value > X >= value
  );
};

export const getUnboundedHighRangeText = (
  axis: Axis, axisOrder: MatrixModel['axis']['x' | 'y']['order'],
) => (
  axisOrder === 'asc'
    ? `=< ${axis}` // "value =< X"
    : `${axis} >` // "X > value"
);

export const getUnboundedLowRangeText = (
  axis: Axis, axisOrder: MatrixModel['axis']['x' | 'y']['order'],
) => (
  axisOrder === 'asc'
    ? `${axis} <` // "X < value"
    : `> ${axis}` // " value > X"
);

export const isValueUnboundedHighRange = value => +value === AXIS_HIGH_UNBOUNDED_NUMBER;
export const isValueUnboundedLowRange = value => +value === AXIS_LOW_UNBOUNDED_NUMBER;

export const addToAxisHeader = ({
  axisHeader, axisHeaderDataType,
}: MatrixModel['axis']['x' | 'y']) => {
  const updatedAxisHeader = [...axisHeader];

  if (axisHeaderDataType === 'number') {
    const lastElement = axisHeader[axisHeader.length - 1];
    if (
      isValueUnboundedHighRange(lastElement)
      || isValueUnboundedLowRange(lastElement)
    ) {
      updatedAxisHeader.splice(axisHeader.length - 1, 0, null);
      return updatedAxisHeader;
    }
  }

  updatedAxisHeader.push(null);
  return updatedAxisHeader;
};

export const insertOneHeaderRow = ({
  axisHeader, rowIndex,
}: {
  axisHeader: MatrixModel['axis']['y']['axisHeader'];
  rowIndex: number;
}) => {
  const updatedAxisHeader = [...axisHeader];

  const lastElement = updatedAxisHeader[updatedAxisHeader.length - 1];
  const firstElement = updatedAxisHeader[0];

  // special case for inserting above an unbound first matrix row
  if (
    (isValueUnboundedHighRange(firstElement) || isValueUnboundedLowRange(firstElement))
    && rowIndex === 0
  ) {
    updatedAxisHeader.splice(rowIndex + 1, 0, null);
    return updatedAxisHeader;
  }
  let unboundLastElement = null;
  if (isValueUnboundedHighRange(lastElement) || isValueUnboundedLowRange(lastElement)) {
    unboundLastElement = updatedAxisHeader.pop();
  }

  updatedAxisHeader.splice(rowIndex, 0, null);

  if (unboundLastElement) { updatedAxisHeader.push(unboundLastElement); }

  return updatedAxisHeader;
};

export const deleteOneHeaderRow = ({
  axisHeader, rowIndex,
}: {
  axisHeader: MatrixModel['axis']['y']['axisHeader'];
  rowIndex: number;
}) => {
  const updatedAxisHeader = [...axisHeader];
  const lastElement = updatedAxisHeader[updatedAxisHeader.length - 1];

  if (
    rowIndex === axisHeader.length - 1
    && (
      isValueUnboundedHighRange(lastElement) || isValueUnboundedLowRange(lastElement)
    )
  ) {
    updatedAxisHeader.splice(rowIndex - 1, 1);
  }

  updatedAxisHeader.splice(rowIndex, 1);

  return updatedAxisHeader;
};

export const getIsDeleteDisabled = ({
  axisHeader, matrixLength,
}: {
  axisHeader: MatrixModel['axis']['y']['axisHeader'];
  matrixLength: number;
}) => {
  const firstElement = axisHeader[0];
  const lastElement = axisHeader[axisHeader.length - 1];

  const firstElementUnbound = (
    isValueUnboundedHighRange(firstElement) || isValueUnboundedLowRange(firstElement)
  );
  const lastElementUnbound = (
    isValueUnboundedHighRange(lastElement) || isValueUnboundedLowRange(lastElement)
  );

  if (
    matrixLength === 1
    || (
      firstElementUnbound
      && lastElementUnbound
      && matrixLength === 2
    )
  ) return true;

  return false;
};

export const deleteLastAxisHeader = ({
  axisHeader, axisHeaderDataType,
}) => {
  const updatedAxisHeader = [...axisHeader];

  if (axisHeaderDataType === 'number') {
    const lastElement = axisHeader[axisHeader.length - 1];
    if (
      isValueUnboundedHighRange(lastElement)
      || isValueUnboundedLowRange(lastElement)
    ) {
      updatedAxisHeader.splice(axisHeader.length - 2, 1);
      return updatedAxisHeader;
    }
  }

  updatedAxisHeader.pop();
  return updatedAxisHeader;
};
